/**
 * AttackPathTable — DataTable + TableToolbar wrapper for Step 5.
 *
 * Attack-vector filtering goes through the shared `FilterDrawer`
 * primitive (multi-select side panel, one row per vector). Mirror
 * of ThreatTable but with attack_vector as the filter dimension.
 *
 * Synthetic `_rowKey` — child TARAs can collide on `attack_path_id`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TableToolbar,
  TablePagination,
  RowActionsMenu,
} from '../../../../components/ui/DataTable';
import FilterDrawer from '../../../../components/ui/DataTable/FilterDrawer';
import { Icon } from '../../../../components/ui/Icon';
import { Button } from '../../../../components/ui/Button2';
import {
  ATTACK_PATH_COLUMNS_READ,
  ATTACK_PATH_COLUMNS_EDIT,
  ATTACK_PATH_ACTIONS_COLUMN_WIDTH,
  ATTACK_VECTORS,
  FEASIBILITY_OPTIONS,
  type AttackPathRecord,
} from './attackPathFieldSchema';
import styles from './AttackPathTable.module.css';

const DataTableAny = DataTable as unknown as React.ComponentType<any>;
const TableToolbarAny = TableToolbar as unknown as React.ComponentType<any>;
const TablePaginationAny = TablePagination as unknown as React.ComponentType<any>;
const RowActionsMenuAny = RowActionsMenu as unknown as React.ComponentType<any>;
const FilterDrawerAny = FilterDrawer as unknown as React.ComponentType<any>;
// Button2 is a JSX forwardRef primitive; cast for use from TSX (mirrors the
// ButtonAny convention across the step-components).
const ButtonAny = Button as unknown as React.ComponentType<any>;

const DEFAULT_PAGE_SIZE = 20;

interface SortState {
  column: string;
  direction: 'asc' | 'desc';
}

export interface AttackPathTableProps {
  paths: AttackPathRecord[];
  loading?: boolean;
  emptyMessage?: string;
  editable?: boolean;
  onAdd?: () => void;
  onEdit?: (path: AttackPathRecord) => void;
  onDelete?: (path: AttackPathRecord) => void;
  /** Export the current filtered/sorted attack-path list to CSV. */
  onExport?: () => void;
  /** Receive a parsed import file (CSV or JSON) for bulk-add. */
  onImport?: (file: File) => void;
}

function sortPaths(rows: AttackPathRecord[], sort: SortState | null): AttackPathRecord[] {
  if (!sort) return rows;
  const dir = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = (a as Record<string, unknown>)[sort.column];
    const bv = (b as Record<string, unknown>)[sort.column];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    return String(av).localeCompare(String(bv)) * dir;
  });
}

function searchPaths(rows: AttackPathRecord[], query: string): AttackPathRecord[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) =>
    Object.values(r).some((v) =>
      v == null
        ? false
        : Array.isArray(v)
        ? v.join(' ').toLowerCase().includes(q)
        : String(v).toLowerCase().includes(q),
    ),
  );
}

export default function AttackPathTable({
  paths,
  loading,
  emptyMessage = 'No attack paths analysed yet.',
  editable = false,
  onAdd,
  onEdit,
  onDelete,
  onExport,
  onImport,
}: AttackPathTableProps) {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [vectorValues, setVectorValues] = useState<string[]>([]);
  const [feasibilityValues, setFeasibilityValues] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const vectorCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const v of ATTACK_VECTORS) counts[v.value] = 0;
    for (const p of paths) {
      const k = String(p.attack_vector ?? '');
      if (k in counts) counts[k] += 1;
    }
    return counts;
  }, [paths]);

  const feasibilityCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const f of FEASIBILITY_OPTIONS) counts[f.value] = 0;
    for (const p of paths) {
      const k = String(p.attack_feasibility_rating ?? '');
      if (k in counts) counts[k] += 1;
    }
    return counts;
  }, [paths]);

  const vectorFiltered = useMemo(
    () => (vectorValues.length === 0
      ? paths
      : paths.filter((p) => vectorValues.includes(String(p.attack_vector ?? '')))),
    [paths, vectorValues],
  );
  const feasibilityFiltered = useMemo(
    () => (feasibilityValues.length === 0
      ? vectorFiltered
      : vectorFiltered.filter((p) => feasibilityValues.includes(String(p.attack_feasibility_rating ?? '')))),
    [vectorFiltered, feasibilityValues],
  );
  const searched = useMemo(() => searchPaths(feasibilityFiltered, search), [feasibilityFiltered, search]);
  const sorted = useMemo(() => sortPaths(searched, sort), [searched, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const paged = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize],
  );

  // Synthetic key — child TARAs collide on attack_path_id.
  const keyed = useMemo(
    () =>
      paged.map((r, i) => ({
        ...r,
        _rowKey: `${r.attack_path_id ?? 'row'}::${(page - 1) * pageSize + i}`,
      })),
    [paged, page, pageSize],
  );

  const columns = useMemo(() => {
    if (!editable) return ATTACK_PATH_COLUMNS_READ;
    return [
      ...ATTACK_PATH_COLUMNS_EDIT,
      {
        key: '_actions',
        label: '',
        align: 'right',
        width: ATTACK_PATH_ACTIONS_COLUMN_WIDTH,
        sortable: false,
        render: (row: AttackPathRecord) => (
          <RowActionsMenuAny
            ariaLabel={`Actions for ${row.attack_path_id}`}
            actions={[
              { id: 'edit', label: 'Edit', icon: <Icon name="edit" size={14} />, onClick: () => onEdit?.(row) },
              { id: 'delete', label: 'Delete', icon: <Icon name="delete" size={14} />, danger: true, onClick: () => onDelete?.(row) },
            ]}
          />
        ),
      },
    ];
  }, [editable, onEdit, onDelete]);

  const vectorFilter = {
    key: 'attack_vector',
    label: 'Vector',
    values: vectorValues,
    onChange: (next: string[]) => {
      setVectorValues(next);
      setPage(1);
    },
    options: ATTACK_VECTORS.map((v) => ({
      value: v.value,
      label: v.label,
      count: vectorCounts[v.value] ?? 0,
    })),
  };

  const feasibilityFilter = {
    key: 'attack_feasibility_rating',
    label: 'Feasibility',
    values: feasibilityValues,
    onChange: (next: string[]) => {
      setFeasibilityValues(next);
      setPage(1);
    },
    options: FEASIBILITY_OPTIONS.map((f) => ({
      value: f.value,
      label: f.label,
      count: feasibilityCounts[f.value] ?? 0,
    })),
  };

  const toolbarActions = (
    <div className={styles.toolbarActions}>
      {onExport && sorted.length > 0 && (
        <ButtonAny variant="outline" size="sm" onClick={onExport}>
          <Icon name="upload" size={14} /> Export CSV
        </ButtonAny>
      )}
      {editable && onImport && (
        <>
          <ButtonAny
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Icon name="download" size={14} /> Import
          </ButtonAny>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onImport(f);
              if (e.target) e.target.value = '';
            }}
          />
        </>
      )}
      {editable && onAdd && (
        <ButtonAny variant="default" size="sm" onClick={onAdd}>
          <Icon name="add" size={14} /> Add Attack Path
        </ButtonAny>
      )}
    </div>
  );

  return (
    <div className={styles.tableShell}>
      <FilterDrawerAny
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={[vectorFilter, feasibilityFilter]}
      />
      <DataTableAny
        columns={columns}
        data={keyed}
        rowKey="_rowKey"
        sort={sort}
        onSortChange={(column: string | null, direction: 'asc' | 'desc' | null) =>
          setSort(column && direction ? { column, direction } : null)
        }
        loading={loading}
        emptyMessage={emptyMessage}
        toolbar={
          <TableToolbarAny
            search={{
              value: search,
              onChange: (v: string) => {
                setSearch(v);
                setPage(1);
              },
              placeholder: 'Search attack paths…',
            }}
            count={{ value: sorted.length, label: 'Paths' }}
            onFilterToggle={() => setFilterOpen((o) => !o)}
            filterButtonActive={filterOpen || vectorValues.length > 0 || feasibilityValues.length > 0}
            actions={toolbarActions}
          />
        }
        pagination={
          <TablePaginationAny
            currentPage={page}
            totalItems={sorted.length}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(size: number) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        }
      />
    </div>
  );
}
